// Which High-Five site a pupil probably belongs on.
//
// There is deliberately NO per-environment host table here. The country is read
// off the host's TLD and redirecting swaps only that suffix, so every host shape
// carries through untouched:
//
//   leerling.ikhighfive.be     -> leerling.ikhighfive.nl
//   dev.leerling.ikhighfive.be -> dev.leerling.ikhighfive.nl
//
// That means dev, acceptance, production and any future subdomain all work with
// no configuration — and there is no way for a mismatched table to make the
// popup fire against the host it is already on. It also replaces the old
// window.environment.localeConfigs, which was only ever defined in public/env.js
// and so left this feature dead in every deployed environment.
//
// Everything here is pure: no React, no side effects.

export type Country = "be" | "nl";

export interface CountryInfo {
	country: Country;
	/** Host suffix that identifies this country, and what we swap in when redirecting. */
	tld: string;
	locale: string;
	region: string;
	timezone: string;
	emoji: string;
}

const COUNTRIES: CountryInfo[] = [
	{
		country: "be",
		tld: ".be",
		locale: "nl-BE",
		region: "BE",
		timezone: "Europe/Brussels",
		emoji: "🇧🇪",
	},
	{
		country: "nl",
		tld: ".nl",
		locale: "nl-NL",
		region: "NL",
		timezone: "Europe/Amsterdam",
		emoji: "🇳🇱",
	},
];

const byCountry: (country: Country) => CountryInfo = (country) => (
	COUNTRIES.find((candidate) => (candidate.country === country)) as CountryInfo
);

/** Lowercase and drop any :port, leaving the bare hostname. */
const bareHost: (host: string) => string = (host) => host.toLowerCase().split(":")[0];

/** The country this host serves, or null when it isn't one of ours (localhost). */
export const getCountryFromHost: (host: string) => CountryInfo | null = (host) => (
	COUNTRIES.find((candidate) => bareHost(host).endsWith(candidate.tld)) || null
);

/** Same host, other country: only the trailing .be/.nl changes. Any port is preserved. */
export const swapCountryHost: (host: string, target: CountryInfo) => string = (host, target) => {
	const [name, port] = host.toLowerCase().split(":");
	const swapped = name.replace(/\.(be|nl)$/, target.tld);

	return port ? `${swapped}:${port}` : swapped;
};

/** What we show the pupil — the pretty domain, without www. or a port. */
export const toLabel: (host: string) => string = (host) => bareHost(host).replace(/^www\./, "");

/**
 * Best country for the browser's languages, in preference order.
 *
 * Matches an exact tag first (nl-BE), then falls back to the region subtag so
 * en-NL and fr-BE still resolve. A bare "nl" is deliberately left unmatched: it
 * is ambiguous between nl-BE and nl-NL, and guessing would be wrong for half the
 * pupils. Those fall through to the timezone signal instead.
 */
export const getCountryForLanguages: (languages: readonly string[]) => CountryInfo | null = (languages) => {
	for (let index = 0; index < languages.length; index += 1) {
		const tag = languages[index].toLowerCase();

		const exact = COUNTRIES.find((candidate) => (candidate.locale.toLowerCase() === tag));
		if (exact) { return exact; }

		const region = tag.split("-")[1];

		if (region) {
			const matchedRegion = COUNTRIES.find((candidate) => (candidate.region.toLowerCase() === region));
			if (matchedRegion) { return matchedRegion; }
		}
	}

	return null;
};

/** Best country for the browser's IANA timezone. */
export const getCountryForTimezone: (timezone: string) => CountryInfo | null = (timezone) => (
	COUNTRIES.find((candidate) => (candidate.timezone === timezone)) || null
);

export interface RedirectSignals {
	host: string;
	languages: readonly string[];
	timezone: string;
}

export interface RedirectSuggestion {
	current: CountryInfo;
	target: CountryInfo;
	/** Where the "let's go" button sends them. */
	targetHost: string;
	/** The domain we show in the copy. */
	targetLabel: string;
}

// On localhost the popup always shows so it can be worked on locally. There is
// nowhere sensible to redirect to, so the button keeps you on the same host; the
// copy borrows the production domain of whichever country we'd be sending you to,
// so the label and the flag always agree.
const previewLabel: (target: CountryInfo) => string = (target) => `leerling.ikhighfive${target.tld}`;

/**
 * Suggest a hop to the other country's site, or null to stay put.
 *
 * Null whenever we aren't confident: no usable timezone signal, already in the
 * right place, or language and timezone disagreeing — a pupil with a Dutch
 * browser in Brussels shouldn't be nagged.
 *
 * Exception: a host with no .be/.nl suffix is a local build, where we always show
 * the popup so it is actually reachable during development.
 */
export const resolveRedirect: (signals: RedirectSignals) => RedirectSuggestion | null = ({ host, languages, timezone }) => {
	const current = getCountryFromHost(host);

	if (!current) {
		const target = getCountryForTimezone(timezone) || byCountry("nl");
		const other = target.country === "be" ? byCountry("nl") : byCountry("be");

		return {
			current: other,
			target,
			targetHost: host,
			targetLabel: previewLabel(target),
		};
	}

	const languageCountry = getCountryForLanguages(languages);
	const timezoneCountry = getCountryForTimezone(timezone);

	// Language alone is too weak a signal to move someone on.
	if (!timezoneCountry) { return null; }

	// Conflicting signals — stay put rather than guess.
	if (languageCountry && languageCountry.country !== timezoneCountry.country) { return null; }

	if (timezoneCountry.country === current.country) { return null; }

	const targetHost = swapCountryHost(host, timezoneCountry);

	return {
		current,
		target: timezoneCountry,
		targetHost,
		targetLabel: toLabel(targetHost),
	};
};
